import { Injectable } from '@angular/core'

import { PageInfo } from '../../common'
import { ADMIN_API_ENDPOINTS } from '../../common/api-endpoints'
import {
  EVENT_ACTIVITY_STATUS,
  ChallengeCapabilitiesDto,
  ChallengeDto,
  ChallengeMallDto,
  ChallengeMilestoneDto,
  ChallengeRewardOptionDto,
  ChallengeRewardTierDto,
  ChallengeTenantOptionDto,
  FindChallengeDto,
  MilestoneEventOptionDto,
  PageResultDto,
  REWARD_CODE_SOURCE,
  REWARD_TYPE,
  RewardCodeImportResultDto,
  SaveChallengeDto,
  TranslationDto,
  UpdateRewardQuantityDto,
  ValidateRewardCodeImportDto,
  isMallScopedMilestone,
  isPurchaseMilestone,
  localizedToTranslation,
  resolveEventActivityStatus,
  toApiDateTime,
  toApiTranslations,
  toBridgedStatus,
  toTranslation
} from '../../dto'
import { HttpService } from '../http.service'

import { MallPublicService } from './mall-public.service'

/**
 * Stamp Collection — Challenge CRUD (FR-01, FR-02, FR-03, FR-04).
 *
 * ✅ Swagger đủ 23/23 API xác nhận 2026-08-25 (domain `admin-api.amlpshop.rikkeidigital.asia`).
 *   #9  GET    /stamp-collection/challenges                          → list()
 *   #10 GET    /stamp-collection/challenges/:id                      → getById()
 *   #11 POST   /stamp-collection/challenges                          → create()
 *   #12 PUT    /stamp-collection/challenges/:id                      → update()
 *   #13 PATCH  /stamp-collection/challenges/:id/visibility           → setVisibility()
 *   #14 DELETE /stamp-collection/challenges/:id                      → delete()
 *   #3  POST   /stamp-collection/challenges/reward-codes/validate-import
 *                                                                    → validateRewardCodeImport()
 *
 * Schema thật của #10/#11/#12 lệch đáng kể so với `API Design.md` ban đầu — xem
 * `docs/stamp-collection/SWAGGER-DIFF-2.md` trước khi sửa file này: `status` đơn đã bị thay
 * bằng `timePhase`+`lifecycleStatus`; `isVisible` không còn trong body Create/Update;
 * Reward Option đổi `numberOfRewards`→`quantity`, bỏ `pool{codes,fileId}`; Update có thêm
 * mảng riêng `rewardQuantities[]` cho option đã tồn tại.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminChallengeService {

  /** Cache tên Mall để join `mallIds[]` → `malls[]` (BE chỉ trả id). */
  private mallNameCache?: Map<string, string>

  constructor(
    private httpService: HttpService,
    private mallPublicService: MallPublicService
  ) {}

  /** Link tải file mẫu Reward Code — ghép cùng base URL với các API khác. */
  public get rewardCodeTemplateUrl(): string {
    return this.httpService.serverURL + ADMIN_API_ENDPOINTS.REWARD_CODE_TEMPLATE
  }

  /**
   * Option Mall cho bộ lọc và cho phần chọn Mall của Challenge.
   * ✅ Luôn lấy từ API Mall có sẵn của hệ thống (`MallPublicService`) — không phụ thuộc
   * vào 23 API stamp-collection.
   */
  public async listMallOptions(): Promise<Array<{ id: string; name: string }>> {
    const result = await this.mallPublicService.getAllMalls()

    return (result?.rows || []).map(mall => ({ id: mall.id, name: mall.name || '--' }))
  }

  /**
   * FR-11-BR-01/02 — dropdown Challenge của màn Report: Challenge đã tạo trên Admin portal,
   * **phân trang** bằng `limit`/`offset` — dropdown cuộn tới cuối thì tải trang kế tiếp,
   * gõ tìm kiếm thì gửi `search` để BE lọc trên toàn bộ Challenge.
   *
   * ⚠️ `mallId` là **bắt buộc với role theo Mall** — Role-Permission FR-11 áp dụng theo
   * FR-06/FR-07: Mall Admin / Mall Sales / Counter staff chỉ được thấy challenge link với
   * Mall của mình. KHÔNG dựa vào actor scope của BE: `ChallengeListComponent` cũng tự gửi
   * `mallId` của user chứ không để BE tự cắt, giữ nguyên cách đó cho nhất quán.
   *
   * API #9 trả `name` là string đã localize theo `Accept-Language` — dùng thẳng, không bọc
   * `TranslationDto` như `toChallengeRow()` vì đây chỉ là nhãn hiển thị của dropdown.
   */
  public async listChallengeOptions(params: {
    mallId?: string
    search?: string
    page: number
    pageSize: number
  }): Promise<{ rows: Array<{ id: string; name: string }>; count: number }> {
    const queryParams: any = {
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize
    }

    if (params.mallId) queryParams.mallId = params.mallId
    if (params.search) queryParams.search = params.search

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.CHALLENGE,
      queryParams
    )

    return {
      rows: (result?.rows || []).map(row => ({ id: row?.id, name: row?.name || '--' })),
      count: result?.count || 0
    }
  }

  /**
   * FR-03-BR-05 — Event để gán cho Milestone Check-in Event.
   *
   * BE **không có** endpoint `challenges/event-options` (đã probe: đường dẫn đó chỉ trúng
   * route `GET /challenges/:id`). Dùng API Event có sẵn của hệ thống:
   * `GET /admin/v1/events?mallIds&getAll`.
   *
   * Chỉ Event có trạng thái **khác "Took place"** (Happening + Upcoming) mới gán được, và
   * chỉ Event thuộc Mall của Challenge (`mallIds`).
   *
   * Không gửi `activityStatus` vì param này chỉ nhận **một** giá trị, không lọc được 2 trạng
   * thái cùng lúc. Lọc ở FE bằng `resolveEventActivityStatus()` — response không trả
   * `activityStatus` (xem `EVENT_ACTIVITY_STATUS`), và đây cũng là mốc mà chip trạng thái màn
   * Event list đang dùng.
   */
  public async listEventOptions(mallIds: string[]): Promise<MilestoneEventOptionDto[]> {
    if (!mallIds?.length) {
      return []
    }

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.EVENTS,
      {
        mallIds: mallIds.join(','),
        getAll: true
      }
    )

    return (result?.rows || [])
      .filter(
        event =>
          resolveEventActivityStatus(event?.startDate, event?.endDate) !==
          EVENT_ACTIVITY_STATUS.EXPIRED
      )
      .map(event =>
        MilestoneEventOptionDto.create({
          id: event?.id,
          name: event?.title,
          mallId: (event?.malls || [])[0]?.id || event?.mallId
        })
      )
  }

  /**
   * Gian hàng **Online** (FK `Tenant`) — Milestone ONLINE_SHOPPING.
   *
   * Lấy **toàn bộ** Tenant online, không lọc theo Mall: mọi Tenant online đều nằm chung 1 Mall
   * mặc định và Online shopping không xét Mall (`isMallScopedMilestone()`). Lọc theo Mall của
   * Challenge thì Challenge không chứa Mall mặc định đó sẽ không chọn được Tenant nào.
   *
   * Chỉ giữ Tenant đang được admin bật (`adminEnabled === true`).
   */
  public async listTenantOptions(): Promise<ChallengeTenantOptionDto[]> {
    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.TENANTS,
      { getAll: true }
    )

    return (result?.rows || [])
      .filter(tenant => !!tenant?.id && tenant.adminEnabled === true)
      .map(tenant =>
        ChallengeTenantOptionDto.create({
          id: tenant.id,
          name: this.toOptionName(tenant),
          mallId: tenant.mallId,
          isOffline: false
        })
      )
  }

  /**
   * Gian hàng **Offline** (FK `OfflineTenant`) — Milestone MALL_SHOPPING / FOOD_ORDERING
   * và Reward Option. Dùng `GET /admin/v1/offline-tenants?mallIds&getAll&enabled=true`
   * (API này nhận **mảng** mallIds).
   */
  public async listOfflineTenantOptions(mallIds: string[]): Promise<ChallengeTenantOptionDto[]> {
    if (!mallIds?.length) {
      return []
    }

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.OFFLINE_TENANTS,
      { mallIds: mallIds.join(','), getAll: true, enabled: true }
    )

    return (result?.rows || []).map(tenant =>
      ChallengeTenantOptionDto.create({
        id: tenant?.id,
        name: this.toOptionName(tenant),
        // Response trả Mall dạng object `mall: {id, name}`, không có `mallId` phẳng — thiếu
        // `mallId` thì bộ lọc gian hàng theo Mall của Milestone giữ lại mọi gian hàng.
        mallId: tenant?.mallId || tenant?.mall?.id,
        isOffline: true
      })
    )
  }

  /**
   * API #9 — FR-01-BR-01/03: danh sách Challenge, lọc theo Mall / Status / tên.
   *
   * ✅ B1 ĐÃ ĐÓNG (response thật 2026-09-10): row trả đủ `challengeStartAt`,
   * `challengeEndAt`, `challengeImageId` + object `image{id,url,name,type}`, và
   * `rewardRemaining` — không còn phải chờ BE cho card ở `challenge-item.component.html`.
   */
  public async list(
    params: FindChallengeDto
  ): Promise<{ rows: ChallengeDto[]; count: number; pageInfo: PageInfo }> {
    const page = params.page || 1
    const pageSize = params.pageSize || 10

    const queryParams: any = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    if (params.mallId) queryParams.mallId = params.mallId
    if (params.status) queryParams.status = params.status
    if (params.search) queryParams.search = params.search
    // API #9 hỗ trợ from/to — UI list chưa có control, gửi khi có
    if (params.fromDate) queryParams.from = params.fromDate
    if (params.toDate) queryParams.to = params.toDate

    const result = await this.httpService.get<PageResultDto<any>>(
      ADMIN_API_ENDPOINTS.CHALLENGE,
      queryParams
    )

    const mallNames = await this.getMallNameMap()
    const rows = (result?.rows || []).map(row => this.toChallengeRow(row, mallNames))

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result.count

    return { rows, count: result.count, pageInfo }
  }

  /** API #10 — Challenge đầy đủ kèm Milestone và Reward Tier. */
  public async getById(id: string): Promise<ChallengeDto> {
    const raw = await this.httpService.get<any>(`${ADMIN_API_ENDPOINTS.CHALLENGE}/${id}`)
    const mallNames = await this.getMallNameMap()

    return this.toChallengeDetail(raw, mallNames)
  }

  /**
   * API #11 — tạo Challenge trong 1 transaction.
   *
   * ⚠️ Swagger 2026-08-25: body **không còn `isVisible`** — BE tự đặt mặc định khi tạo mới.
   * Theo quyết định 2026-08-25: mặc định coi là **Visible**. Nếu admin chọn Hidden trên form
   * thì gọi thêm API #13 (`PATCH .../visibility`) ngay sau khi Create thành công.
   */
  public async create(data: SaveChallengeDto): Promise<ChallengeDto> {
    const raw = await this.httpService.post<any>(
      ADMIN_API_ENDPOINTS.CHALLENGE,
      this.toCreateBody(data)
    )

    // ⚠️ CỨNG HOÁ 2026-08-25: gọi PATCH visibility **luôn**, không chỉ khi Hidden. Quyết định
    // "mặc định Visible" giả định BE tự tạo Visible — nếu giả định đó sai (BE mặc định
    // Hidden), bỏ qua PATCH cho case Visible sẽ để lọt challenge bị ẩn ngoài ý muốn admin.
    // Gọi luôn để đúng bất kể BE mặc định gì, đổi lấy 1 request phụ không đáng kể.
    if (raw?.id) {
      const isVisible = data.isVisible ?? true
      await this.setVisibility(raw.id, isVisible)
      raw.isVisible = isVisible
    }

    const mallNames = await this.getMallNameMap()

    return this.toChallengeDetail(raw, mallNames)
  }

  /**
   * API #12 — cập nhật theo lifecycle edit matrix.
   *
   * ⚠️ `milestones[].id` là optional với BE: **có id = update, không id = tạo mới**. Nếu drop
   * id thì BE xoá và tạo lại ⇒ mất toàn bộ Reward Code đã phát của Reward Option liên quan.
   *
   * ⚠️ Reward Option đổi cơ chế hoàn toàn — xem `toUpdateBody()`:
   *   - Option **đã có `id`**: `quantity`/`fileId` KHÔNG gửi trong `rewardTiers[]` nữa, đi qua
   *     mảng riêng `rewardQuantities[]`.
   *   - Option **mới** (không `id`): `quantity`/`fileId` gửi inline như lúc Create.
   *
   * ⚠️ Body **không còn `isVisible`** — gọi thêm API #13 sau Update khi admin đổi Display
   * status trên form (trước đây gộp trong 1 request PUT duy nhất).
   *
   * @param originalIsVisible `isVisible` lúc mở form. Không truyền (`undefined`) thì vẫn gọi
   *   API #13 vì không biết giá trị đang lưu.
   */
  public async update(
    id: string,
    data: SaveChallengeDto,
    originalIsVisible?: boolean
  ): Promise<ChallengeDto> {
    const raw = await this.httpService.put<any>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/${id}`,
      this.toUpdateBody(data)
    )

    const isVisible = !!data.isVisible

    if (isVisible !== originalIsVisible) {
      await this.setVisibility(id, isVisible)
    }

    raw.isVisible = isVisible

    const mallNames = await this.getMallNameMap()

    return this.toChallengeDetail(raw, mallNames)
  }

  /**
   * API #14 — FR-01-BR-06.
   * ⚠️ BE là **soft-delete** ("Soft-delete an eligible PLANNED Challenge"), không phải
   * xoá cứng như giả định ban đầu. Response `{success: boolean}`.
   */
  public async delete(id: string): Promise<void> {
    await this.httpService.delete<{ success: boolean }>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/${id}`
    )
  }

  /**
   * API #13 — FR-01-BR-07: bật/tắt `Challenge.isVisible`, không đổi lifecycle status.
   * ⚠️ Method là **PATCH** (trước FE dùng PUT) và response là `{id, isVisible}`
   * (trước FE khai `boolean`).
   */
  public async setVisibility(id: string, isVisible: boolean): Promise<void> {
    await this.httpService.patch<{ id: string; isVisible: boolean }>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/${id}/visibility`,
      { isVisible }
    )
  }

  /**
   * API #3 — FR-04-BR-06: validate file XLSX chứa Reward Code trước khi Save.
   * Kiểm định dạng, format code, trùng lặp và capacity của Reward Option. **Không persist.**
   *
   * Luồng đầy đủ: upload file (file service chung) → gọi hàm này → nếu OK thì gửi
   * `options[].pool.fileId` trong API #11/#12.
   *
   * ✅ ĐÃ CHỐT cách hiển thị kết quả — xem `RewardCodeImportResultDto` và
   * `RewardOptionFormComponent.handleCodeFileSelect()`.
   */
  public async validateRewardCodeImport(
    data: ValidateRewardCodeImportDto
  ): Promise<RewardCodeImportResultDto> {
    return await this.httpService.post<RewardCodeImportResultDto>(
      `${ADMIN_API_ENDPOINTS.CHALLENGE}/reward-codes/validate-import`,
      data
    )
  }

  // ===============================================================================================
  // Mapping BE → FE
  // ===============================================================================================

  /**
   * API #9 row → `ChallengeDto`.
   * `name`/`description` là string đã localize theo `Accept-Language` (không phải
   * `translations[]` như API #10) ⇒ bọc bằng `localizedToTranslation`.
   */
  /**
   * API #9 row → `ChallengeDto`.
   *
   * ⚠️ Schema response chưa xác nhận được (Swagger dangling `$ref` `AdminChallengeListItemDto`
   * — xem `docs/stamp-collection/AUDIT-FINAL.md` §3). Query filter `status` của API #9 vẫn
   * dùng 3 giá trị cũ, nhưng domain model của BE đã chuyển sang `timePhase`+`lifecycleStatus`
   * ở API #10/#11/#12 — map cả 2 dạng phòng khi List cũng trả theo model mới; `status` giữ
   * lại làm fallback nếu chỉ có field cũ.
   */
  private toChallengeRow(raw: any, mallNames: Map<string, string>): ChallengeDto {
    return ChallengeDto.create({
      id: raw?.id,
      name: localizedToTranslation(raw?.name),
      description: localizedToTranslation(raw?.description),
      // ⚠️ Response thật 2026-09-10: row **không có** field `status`, chỉ có `timePhase` —
      // giống API #10. Phải bắc cầu, nếu không `canDeleteChallenge()` nhận `undefined` và
      // nút Delete bị disable vĩnh viễn kể cả với Challenge `PLANNED`.
      status: toBridgedStatus(raw?.timePhase),
      timePhase: raw?.timePhase,
      lifecycleStatus: raw?.lifecycleStatus,
      capabilities: raw?.capabilities ? ChallengeCapabilitiesDto.create(raw.capabilities) : undefined,
      isVisible: raw?.isVisible,
      createdAt: raw?.createdAt,
      malls: this.toMalls(raw?.mallIds, mallNames),
      // Dòng thời gian trên card list (`challenge-item.component.html:37-38`)
      challengeStartAt: raw?.challengeStartAt,
      challengeEndAt: raw?.challengeEndAt,
      challengeImageId: raw?.challengeImageId,
      // ✅ Xác nhận bằng response thật 2026-09-10: object file nằm ở key `image`
      // (KHÔNG phải `challengeImage` như tên của `challengeImageId`).
      challengeImageUrl: raw?.image?.url,
      remainingRewards: raw?.rewardRemaining,
      // ⚠️ Response thật không có `canEdit`/`canDelete` như `API Design.md` mô tả — thay
      // bằng `capabilities`. `canUpdate` là tương đương gần nhất của `canEdit`, và cũng là
      // giá trị mà `EditChallengeComponent.readonly` dùng để khoá form ⇒ dùng chung một
      // nguồn, tránh cảnh bấm Edit rồi vào form chỉ đọc.
      canEdit: raw?.capabilities?.canUpdate
      // `canDelete`: `capabilities` KHÔNG có cờ tương ứng ⇒ để undefined, `ChallengeItem`
      // rơi về `canDeleteChallenge(permission, status)` (FR-01-BR-06: chỉ xoá `PLANNED`).
    })
  }

  /**
   * API #10/#11/#12 response → `ChallengeDto`.
   *
   * ✅ Swagger 2026-08-25: response giờ trả CẢ HAI dạng name/description — string đã
   * localize (`raw.name`) **và** `translations[]` đầy đủ 2 ngôn ngữ. Form Edit cần đủ 2 ngôn
   * ngữ nên vẫn dùng `translations[]` qua `toTranslation()`.
   */
  private toChallengeDetail(raw: any, mallNames: Map<string, string>): ChallengeDto {
    const translations = raw?.translations || []

    return ChallengeDto.create({
      id: raw?.id,
      name: toTranslation(translations, 'name'),
      description: toTranslation(translations, 'description'),
      malls: this.toMalls(raw?.mallIds, mallNames),
      challengeImageId: raw?.challengeImageId,
      rewardBannerImageId: raw?.rewardBannerImageId,
      // ✅ Xác nhận bằng response thật 2026-09-10: ảnh challenge ở key `image`, reward
      // banner ở key `rewardBannerImage` — 2 key đặt tên KHÔNG đối xứng nhau.
      challengeImageUrl: raw?.image?.url,
      rewardBannerImageUrl: raw?.rewardBannerImage?.url,
      sequentialMilestones: raw?.sequentialMilestones,
      displayStartAt: raw?.displayStartAt,
      displayEndAt: raw?.displayEndAt,
      challengeStartAt: raw?.challengeStartAt,
      challengeEndAt: raw?.challengeEndAt,
      redemptionStartAt: raw?.redemptionStartAt,
      redemptionEndAt: raw?.redemptionEndAt,
      // ⚠️ `status` đơn không còn tồn tại ở BE — bắc cầu từ `timePhase` (xem mapper).
      status: toBridgedStatus(raw?.timePhase),
      timePhase: raw?.timePhase,
      lifecycleStatus: raw?.lifecycleStatus,
      capabilities: raw?.capabilities ? ChallengeCapabilitiesDto.create(raw.capabilities) : undefined,
      // ✅ ĐÍNH CHÍNH 2026-08-25: `isVisible` VẪN có trong response detail (required field của
      // `ChallengeAggregateDto`) — chỉ riêng body Create/Update mới bỏ field này. Đợt trước đọc
      // nhầm "không có trong body" thành "không có trong response" nên suy diễn sai từ
      // `lifecycleStatus`; đã bỏ, đọc thẳng giá trị BE trả.
      isVisible: raw?.isVisible,
      // ✅ Swagger 2026-08-25: đã có `rewardRemaining` — không cần workaround N2 nữa.
      remainingRewards: raw?.rewardRemaining,
      challengeConfigurationId: raw?.challengeConfigurationId,
      milestones: (raw?.milestones || []).map(item => this.toMilestone(item)),
      rewardTiers: (raw?.rewardTiers || []).map(item => this.toRewardTier(item))
      // ✅ B5 ĐÃ ĐÓNG — API #10 trả object file kèm `url` ở cả 4 tầng: challenge, reward
      // banner, milestone (`image`+`logo`) và reward option.
      // ⚠️ BE-MISSING B3: không embed `challengeConfiguration` (chỉ có id ở trên).
    })
  }

  private toMilestone(raw: any): ChallengeMilestoneDto {
    const translations = raw?.translations || []

    return ChallengeMilestoneDto.create({
      id: raw?.id,
      position: raw?.position,
      type: raw?.type,
      name: toTranslation(translations, 'name'),
      description: toTranslation(translations, 'description'),
      imageId: raw?.imageId,
      // ✅ API #10 trả kèm object file có `url` cho cả 2 — `milestone-item.component.html`
      // đọc `imageUrl`/`logoUrl`, trước đây không map nên luôn về `IMAGES.NO_BANNER`.
      imageUrl: raw?.image?.url,
      logoId: raw?.logoId,
      logoUrl: raw?.logo?.url,
      eventId: raw?.eventId,
      minimumExpense: raw?.minimumExpense,
      firstPurchaseOnly: raw?.firstPurchaseOnly,
      // Challenge cũ chưa có field này → coi là không cho request
      enableRequestStamp: !!raw?.enableRequestStamp,
      qrAvailable: raw?.qrAvailable,
      applyAllMalls: raw?.applyAllMalls,
      mallIds: raw?.mallIds || [],
      applyAllTenants: raw?.applyAllTenants,
      tenantIds: raw?.tenantIds || [],
      offlineTenantIds: raw?.offlineTenantIds || []
      // ⚠️ BE-MISSING: `qrCode` không còn trong API #10 → dùng API #15.
      // ⚠️ BE-MISSING: `eventName` không có → join từ listEventOptions().
    })
  }

  private toRewardTier(raw: any): ChallengeRewardTierDto {
    return ChallengeRewardTierDto.create({
      id: raw?.id,
      position: raw?.position,
      options: (raw?.options || []).map(item => this.toRewardOption(item))
    })
  }

  private toRewardOption(raw: any): ChallengeRewardOptionDto {
    return ChallengeRewardOptionDto.create({
      id: raw?.id,
      type: raw?.type,
      name: toTranslation(raw?.translations || [], 'name'),
      description: toTranslation(raw?.translations || [], 'description'),
      imageId: raw?.imageId,
      // ✅ Như milestone — `reward-option-form.component.html` đọc `imageUrl`.
      imageUrl: raw?.image?.url,
      // ⚠️ Response (đọc) vẫn dùng tên `numberOfRewards`; request (gửi) dùng `quantity` —
      // xem `toRewardOptionBody()`. 2 tên khác nhau giữa 2 chiều, không phải nhầm lẫn.
      numberOfRewards: raw?.numberOfRewards,
      codeSource: raw?.codeSource,
      discountType: raw?.discountType,
      discountValue: raw?.discountValue,
      minimumPurchaseValue: raw?.minimumPurchaseValue,
      maximumDiscountValue: raw?.maximumDiscountValue,
      validityStartAt: raw?.validityStartAt,
      validityEndAt: raw?.validityEndAt,
      mallIds: raw?.mallIds || [],
      offlineTenantIds: raw?.offlineTenantIds || []
      // ⚠️ BE-MISSING: `issuedCount` (FR-04-BR-08) và `codeFiles[]` không có trong API #10.
    })
  }

  /** `mallIds[]` + cache tên → `malls[]` để template đọc `malls[].name.en` như cũ. */
  private toMalls(mallIds: string[], mallNames: Map<string, string>): ChallengeMallDto[] {
    return (mallIds || []).map(mallId =>
      ChallengeMallDto.create({
        id: mallId,
        name: localizedToTranslation(mallNames.get(mallId) || '--')
      })
    )
  }

  /** Tenant/OfflineTenant có thể trả `name` dạng TranslationDto hoặc `translations[]`. */
  private toOptionName(tenant: any): TranslationDto {
    if (tenant?.name?.en !== undefined || tenant?.name?.kh !== undefined) {
      return TranslationDto.create(tenant.name.en, tenant.name.kh)
    }

    if (Array.isArray(tenant?.translations)) {
      return toTranslation(tenant.translations, 'name')
    }

    return localizedToTranslation(tenant?.name)
  }

  private async getMallNameMap(): Promise<Map<string, string>> {
    if (this.mallNameCache) {
      return this.mallNameCache
    }

    const cache = new Map<string, string>()

    try {
      const options = await this.listMallOptions()
      options.forEach(option => cache.set(option.id, option.name))
      this.mallNameCache = cache
    } catch (error) {
      // Không chặn luồng chính nếu API Mall lỗi — tên Mall sẽ hiển thị '--'
    }

    return cache
  }

  // ===============================================================================================
  // Mapping FE → BE
  // ===============================================================================================

  /** `SaveChallengeDto` → body API #11 (`CreateChallengeDto`). Không có `isVisible`. */
  private toCreateBody(data: SaveChallengeDto): any {
    return {
      sequentialMilestones: data.sequentialMilestones,
      challengeImageId: data.challengeImageId,
      rewardBannerImageId: data.rewardBannerImageId,
      // `<input datetime-local>` giữ giờ local không timezone → phải quy về ISO cho BE
      displayStartAt: toApiDateTime(data.displayStartAt),
      displayEndAt: toApiDateTime(data.displayEndAt),
      challengeStartAt: toApiDateTime(data.challengeStartAt),
      challengeEndAt: toApiDateTime(data.challengeEndAt),
      redemptionStartAt: toApiDateTime(data.redemptionStartAt),
      redemptionEndAt: toApiDateTime(data.redemptionEndAt),
      mallIds: data.mallIds || [],
      translations: toApiTranslations(data.name, data.description),
      milestones: (data.milestones || []).map(item => this.toMilestoneBody(item)),
      rewardTiers: (data.rewardTiers || []).map(tier => ({
        position: tier.position,
        // CreateRewardTierDto không có `id` — Challenge mới nên mọi option đều "mới"
        options: (tier.options || []).map(option => this.toRewardOptionBody(option, true))
      }))
    }
  }

  /**
   * `SaveChallengeDto` → body API #12 (`UpdateChallengeDto`). Không có `isVisible`.
   *
   * ⚠️ Tách riêng khỏi `toCreateBody()` vì Reward Option đổi cơ chế khi Update:
   *   - Option đã có `id` → `quantity`/`fileId` đi qua `rewardQuantities[]` (accumulator),
   *     KHÔNG gửi trong `options[]`.
   *   - Option mới (không `id`) → `quantity`/`fileId` gửi inline như Create.
   */
  private toUpdateBody(data: SaveChallengeDto): any {
    const rewardQuantities: UpdateRewardQuantityDto[] = []

    const rewardTiers = (data.rewardTiers || []).map(tier => ({
      id: tier.id,
      position: tier.position,
      options: (tier.options || []).map(option => {
        const isExisting = !!option.id

        if (isExisting) {
          rewardQuantities.push(
            UpdateRewardQuantityDto.create({
              id: option.id,
              quantity: option.numberOfRewards,
              fileId: option.pool?.fileId
            })
          )
        }

        return this.toRewardOptionBody(option, !isExisting)
      })
    }))

    return {
      sequentialMilestones: data.sequentialMilestones,
      challengeImageId: data.challengeImageId,
      rewardBannerImageId: data.rewardBannerImageId,
      displayStartAt: toApiDateTime(data.displayStartAt),
      displayEndAt: toApiDateTime(data.displayEndAt),
      challengeStartAt: toApiDateTime(data.challengeStartAt),
      challengeEndAt: toApiDateTime(data.challengeEndAt),
      redemptionStartAt: toApiDateTime(data.redemptionStartAt),
      redemptionEndAt: toApiDateTime(data.redemptionEndAt),
      mallIds: data.mallIds || [],
      translations: toApiTranslations(data.name, data.description),
      milestones: (data.milestones || []).map(item => this.toMilestoneBody(item)),
      rewardTiers,
      rewardQuantities
    }
  }

  private toMilestoneBody(milestone: ChallengeMilestoneDto): any {
    // Only Mall shopping / Food ordering / Online shopping are scoped by tenant; any
    // tenant data left over on the other types must not reach the API.
    const hasTenantScope = isPurchaseMilestone(milestone.type)
    // Online shopping không xét Mall — dọn luôn Mall cũ đã lưu trước khi bỏ điều kiện này.
    const applyAllMalls = !isMallScopedMilestone(milestone.type) || !!milestone.applyAllMalls
    const applyAllTenants = hasTenantScope && !!milestone.applyAllTenants

    return {
      // Giữ id để BE update thay vì tạo lại (API #12); Create bỏ qua field này nếu có
      id: milestone.id,
      position: milestone.position,
      type: milestone.type,
      imageId: milestone.imageId,
      logoId: milestone.logoId,
      eventId: milestone.eventId || null,
      minimumExpense: milestone.minimumExpense ?? null,
      firstPurchaseOnly: !!milestone.firstPurchaseOnly,
      enableRequestStamp: !!milestone.enableRequestStamp,
      applyAllMalls,
      // `applyAllMalls` / `applyAllTenants` already mean "every one of them", so the
      // explicit list has to stay empty instead of contradicting the flag.
      mallIds: applyAllMalls ? [] : milestone.mallIds || [],
      applyAllTenants,
      tenantIds: hasTenantScope && !applyAllTenants ? milestone.tenantIds || [] : [],
      offlineTenantIds:
        hasTenantScope && !applyAllTenants ? milestone.offlineTenantIds || [] : [],
      translations: toApiTranslations(milestone.name, milestone.description)
    }
  }

  /**
   * `ChallengeRewardOptionDto` → phần tử `options[]` của body Create/Update.
   *
   * ⚠️ Swagger 2026-08-25: request KHÔNG còn `numberOfRewards`/`pool{codes,fileId}` — đổi
   * tên thành `quantity` và bỏ wrapper `pool`, chỉ còn `fileId` phẳng. Response (đọc lại) vẫn
   * dùng tên cũ `numberOfRewards` — 2 tên khác nhau giữa chiều gửi/nhận, xem `toRewardOption()`.
   *
   * @param includeQuantity `true` khi option chưa có `id` (mới, gồm cả lúc Create) — gửi
   *   `fileId` (UPLOADED) hoặc `quantity` (AUTO_GENERATED) ngay trong object này. `false`
   *   khi Update một option đã tồn tại — `quantity`/`fileId` đã được đẩy vào `rewardQuantities[]` ở `toUpdateBody()`, không lặp lại.
   */
  private toRewardOptionBody(option: ChallengeRewardOptionDto, includeQuantity: boolean): any {
    // FR-04-BR-05 — Physical Gift is handed over at the counter, so it carries no tenant
    // scope even if the option previously held one. Mall scope applies to both types
    // (Figma 14791:20306).
    const isPhysical = option.type === REWARD_TYPE.PHYSICAL

    const body: any = {
      type: option.type,
      imageId: option.imageId,
      codeSource: option.codeSource,
      discountType: option.discountType || null,
      discountValue: option.discountValue ?? null,
      minimumPurchaseValue: option.minimumPurchaseValue ?? null,
      maximumDiscountValue: option.maximumDiscountValue ?? null,
      validityStartAt: toApiDateTime(option.validityStartAt),
      validityEndAt: toApiDateTime(option.validityEndAt),
      mallIds: option.mallIds || [],
      offlineTenantIds: isPhysical ? [] : option.offlineTenantIds || [],
      translations: toApiTranslations(option.name, option.description)
    }

    if (includeQuantity) {
      // FR-04-BR-06 — BE trả 400 nếu gửi cả 2: UPLOADED tự tính số lượng từ số code hợp lệ
      // trong file, AUTO_GENERATED sinh code theo `quantity`.
      if (option.codeSource === REWARD_CODE_SOURCE.UPLOADED) {
        body.fileId = option.pool?.fileId
      } else {
        body.quantity = option.numberOfRewards
      }
    } else if (option.id) {
      // `CreateRewardOptionDto` không khai báo `id` — chỉ gửi khi chắc chắn là Update một
      // option đã tồn tại, tránh field lạ bị BE từ chối nếu bật whitelist validation.
      body.id = option.id
    }

    return body
  }
}
